class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"], [data-sx-sheet-close]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    // The sheet is position: fixed; living inside the sticky header would tie it to the header's transform.
    queueMicrotask(() => {
      if (this.parentElement !== document.body) document.body.appendChild(this);
    });

    // Quantity stepper inside the sheet (buttons are rendered later, so delegate).
    this.notification.addEventListener('click', (evt) => {
      const button = evt.target.closest('[data-sx-qty]');
      if (!button) return;
      const item = button.closest('[data-key]');
      const current = parseInt(item.querySelector('.sx-qty span').textContent, 10) || 0;
      this.changeQuantity(item.dataset.key, current + parseInt(button.dataset.sxQty, 10));
    });
  }

  open() {
    this.notification.classList.add('animate', 'active');
    this.classList.add('sx-open');
    document.body.classList.add('sx-sheet-lock');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    this.classList.remove('sx-open');
    document.body.classList.remove('sx-sheet-lock');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.showError('');
    this.renderSections(parsedState.sections);

    if (this.header) this.header.reveal();
    this.open();
    this.applyAutoCode();
  }

  renderSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const html = sections && sections[section.id];
      const target = document.getElementById(section.id);
      if (!html || !target) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner !== null) target.innerHTML = inner;
    });
  }

  changeQuantity(key, quantity) {
    this.notification.classList.add('sx-busy');
    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: key,
        quantity: Math.max(quantity, 0),
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          // Shopify error payload, e.g. more than the available stock.
          this.showError(state.description || state.message || window.cartStrings?.error);
          return;
        }
        this.showError('');
        // Every line is re-rendered with its current key (Shopify re-keys a line when its quantity changes).
        this.renderSections(state.sections);
        if (!state.item_count) {
          this.close();
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-notification', cartData: state });
        }
      })
      .catch(() => this.showError(window.cartStrings?.error))
      .finally(() => this.notification.classList.remove('sx-busy'));
  }

  showError(message) {
    const el = this.notification.querySelector('[data-sx-sheet-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /* Add the first-order code once per visit when the cart has none, so the saving shows here.
     Shares the session flag with the cart page (sections/main-cart-footer.liquid). */
  applyAutoCode() {
    const code = this.dataset.autoCode;
    if (!code) return;
    const key = 'shazeAutoCode:' + code;
    try {
      if (sessionStorage.getItem(key) === '1') return;
    } catch (e) {}

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        if ((cart.discount_codes || []).length > 0) return;
        try {
          sessionStorage.setItem(key, '1');
        } catch (e) {}
        return fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: code,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((state) => this.renderSections(state.sections));
      })
      .catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        // All lines, not only the one just added.
        id: 'cart-notification-product',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!element) return null;
    return element.innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
